import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Divider,
  Chip,
  Card,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssessmentIcon from "@mui/icons-material/Assessment";
import API from "../utils/api";
import SmartTable from "../components/Layout/SmartTable";
import { toast } from "react-toastify";
import LeadStatusCell from "../components/LeadStatusCell";
import { LeadStatusFilterMenuItems } from "../components/LeadStatusMenuItems";
import useAuth from "../hooks/useAuth";

export default function ReportsPage() {
  const theme = useTheme();
  const { isReadOnly } = useAuth();
  const [filters, setFilters] = useState({
    status: "all",
    territory: "all",
    assigned_rep: "all",
    start_date: "",
    end_date: "",
    property_type: "",
  });
  const [territories, setTerritories] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [hasReport, setHasReport] = useState(false);

  useEffect(() => {
    fetchTerritories();
    fetchReps();
  }, []);

  const fetchTerritories = async () => {
    try {
      const res = await API.get("/territories/");
      setTerritories(res.data || []);
    } catch (err) {
      console.error("Error fetching territories:", err);
    }
  };

  const fetchReps = async () => {
    try {
      const res = await API.get("/users/");
      setReps(res.data.filter(u => u.groups?.some(g => 
        typeof g === 'string' ? g.toLowerCase().includes('sales') : g.name?.toLowerCase().includes('sales')
      )) || []);
    } catch (err) {
      console.error("Error fetching reps:", err);
    }
  };

  const handleFilterChange = (field, value) => {
    if (isReadOnly) return;
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset report when filters change
    if (hasReport) {
      setHasReport(false);
      setReportData([]);
    }
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      // Skip empty strings and "all" values
      if (value && value !== "all" && value !== "All Statuses" && value !== "All Territories" && value !== "All Reps") {
        params.append(key, value);
      }
    });
    return params.toString();
  };

  const handleGenerateViewReport = async () => {
    if (isReadOnly) return;
    setLoading(true);
    try {
      const params = buildQueryParams();
      const response = await API.get(`/leads/?${params}`);
      setReportData(response.data || []);
      setHasReport(true);
      toast.success(`✅ Report generated! Found ${response.data?.length || 0} leads.`);
    } catch (err) {
      console.error("Error generating report:", err);
      toast.error("❌ Failed to generate report");
      setReportData([]);
      setHasReport(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (isReadOnly) return;
    setLoading(true);
    try {
      const params = buildQueryParams();

      // Make request with responseType: 'blob' for file download
      const response = await API.get(`/leads/export/?${params}`, {
        responseType: "blob",
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leads_report_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("✅ Report downloaded successfully!");
    } catch (err) {
      console.error("Error downloading report:", err);
      toast.error("❌ Failed to download report");
    } finally {
      setLoading(false);
    }
  };

  // Define columns for SmartTable
  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "first_name", headerName: "First Name", flex: 1 },
    { field: "last_name", headerName: "Last Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.2 },
    { field: "phone", headerName: "Phone", flex: 1 },
    { field: "address", headerName: "Address", flex: 1.5 },
    { field: "city", headerName: "City", flex: 1 },
    { field: "state", headerName: "State", flex: 0.8 },
    { field: "zip_code", headerName: "ZIP", flex: 0.8 },
   {
  field: "status",
  headerName: "Status",
  flex: 1.3,
  align: "left",
  headerAlign: "left",
  sortable: false,
  renderCell: (params) => {
    const value = params?.row?.status || "New";

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",       // 🔥 vertical center
          justifyContent: "flex-start", // 🔥 left align
          pl: 1,                       // optional padding-left
        }}
      >
        <LeadStatusCell status={value} />
      </Box>
    );
  },
},
    { field: "property_type", headerName: "Property Type", flex: 1 },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
      valueGetter: (params) => {
        const price = params?.row?.price;
        return price ? `$${parseFloat(price).toLocaleString()}` : "—";
      },
    },
    { field: "square_feet", headerName: "Sq Ft", flex: 0.8 },
    { field: "year_built", headerName: "Year Built", flex: 0.8 },
    {
      field: "sold_date",
      headerName: "Sold Date",
      flex: 1,
      valueGetter: (params) => {
        const date = params?.row?.sold_date;
        return date ? new Date(date).toLocaleDateString() : "—";
      },
    },
    {
      field: "territory_name",
      headerName: "Territory",
      flex: 1,
      valueGetter: (params) => params?.row?.territory_name || "—",
    },
    {
      field: "assigned_rep_name",
      headerName: "Assigned Rep",
      flex: 1.2,
      valueGetter: (params) => params?.row?.assigned_rep_name || "—",
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 1.2,
      valueGetter: (params) => {
        const date = params?.row?.created_at;
        return date ? new Date(date).toLocaleDateString() : "—";
      },
    },
  ];

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <AssessmentIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Lead Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate and view lead reports based on your criteria, or download as Excel
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Card>

        <Paper sx={{ p: 3, mb: 3 }} data-tour="reports-filters">
        <Typography variant="h6" gutterBottom>
          Filter Criteria
        </Typography>

        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* -----------------------------------Status Filter */}
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              label="Status"
            >
              <LeadStatusFilterMenuItems />
            </Select>
          </FormControl>

          {/* -------------------------------------------------------------------Territory Filter */}
          <FormControl fullWidth>
            <InputLabel>Territory</InputLabel>
            <Select
              value={filters.territory}
              onChange={(e) => handleFilterChange("territory", e.target.value)}
              label="Territory"
            >
              <MenuItem value="all">All Territories</MenuItem>
              {territories.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* --------------------------------------------------------------------Assigned Rep Filter */}
          <FormControl fullWidth>
            <InputLabel>Assigned Rep</InputLabel>
            <Select
              value={filters.assigned_rep}
              onChange={(e) => handleFilterChange("assigned_rep", e.target.value)}
              label="Assigned Rep"
            >
              <MenuItem value="all">All Reps</MenuItem>
              {reps.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.full_name || r.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*------------------------------------------------------------------------ Date Range */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange("start_date", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange("end_date", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          {/*------------------------------------------------------------------------- Property Type */}
          <TextField
            label="Property Type"
            value={filters.property_type}
            onChange={(e) => handleFilterChange("property_type", e.target.value)}
            fullWidth
            placeholder="e.g., Single Family, Condo, etc."
          />

          {/* -------------------------------------------------------------------------Action Buttons */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon />}
              onClick={handleGenerateViewReport}
              disabled={isReadOnly || loading}
              size="large"
              sx={{
                flex: 1,
                opacity: isReadOnly ? 0.6 : 1,
                cursor: isReadOnly ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Generating..." : "Generate & View Report"}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={isReadOnly || loading}
              size="large"
              sx={{
                flex: 1,
                opacity: isReadOnly ? 0.6 : 1,
                cursor: isReadOnly ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Downloading..." : "Download Report (Excel)"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ------------------------------------------------------------------------Report Results Table */}
      {hasReport && (
        <Paper sx={{ p: 3 }} data-tour="reports-results">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">
              Report Results ({reportData.length} leads)
            </Typography>
            <Chip
              label={`${reportData.length} Total`}
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <SmartTable
            rows={reportData}
            columns={columns}
            mobileEssentialFields={["first_name", "last_name", "status", "territory_name"]}
          />
        </Paper>
      )}
      </Box>
    </Box>
  );
}